import React, { useState, useEffect } from "react";
import { Trash2, PlusCircle, RefreshCw } from "lucide-react";
import { TspCard, TspTable, TspBadge } from "../primitives";

// Shared list + inline-add-entry pattern for TSP's master-data pages
// (Contacts, Accommodation, Prasadam). Upgraded to a clean,
// responsive multi-column layout with explicit labels, helper badges,
// and controlled/uncontrolled draft sync.
export default function TspMasterDataCard({
  title,
  action,
  columns,
  fields,
  records,
  emptyDraft,
  initialDraft,
  draft: controlledDraft,
  onDraftChange,
  canManage = false,
  onAdd,
  onDelete,
  rowLabel = () => "entry",
  formTitle,
  formSubtitle,
  topHelper,
}) {
  const [internalDraft, setInternalDraft] = useState(() => initialDraft || emptyDraft);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const draft = controlledDraft !== undefined ? controlledDraft : internalDraft;
  const setDraft = (newDraft) => {
    if (onDraftChange) onDraftChange(newDraft);
    if (controlledDraft === undefined) setInternalDraft(newDraft);
  };

  useEffect(() => {
    if (initialDraft && controlledDraft === undefined) {
      setInternalDraft((previous) => ({ ...previous, ...initialDraft }));
    }
  }, [initialDraft, controlledDraft]);

  const requiredFilled = canManage && fields.length > 0 && fields.every((f) => !f.required || String(draft[f.key] || "").trim() !== "");

  const handleAddEntry = async () => {
    if (!requiredFilled || busy) return;
    setError("");
    setBusy(true);
    try {
      const result = await onAdd(draft);
      if (result.ok) setDraft(emptyDraft);
      else setError(result.message || "Could not save this entry.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError("");
    try {
      const result = await onDelete(id);
      if (!result.ok) setError(result.message || "Could not remove this entry.");
    } finally {
      setDeletingId(null);
    }
  };

  const optionValue = (option) => (typeof option === "object" ? option.value : option);
  const optionLabel = (option) => (typeof option === "object" ? option.label : option);

  const tableColumns = canManage
    ? [
        ...columns,
        {
          key: "delete",
          label: "",
          align: "right",
          render: (row) => (
            <button
              type="button"
              className="tsp-icon-link-btn tsp-row-delete-btn"
              onClick={() => handleDelete(row.id)}
              disabled={deletingId === row.id}
              aria-label={`Remove ${rowLabel(row)}`}
            >
              <Trash2 size={14} />
            </button>
          ),
        },
      ]
    : columns;

  return (
    <div className="tsp-master-data-wrapper">
      <TspCard title={title} action={action || <TspBadge tone="neutral">{records.length} records</TspBadge>}>
        {records.length === 0 ? <p className="tsp-muted-text">No records yet.</p> : <TspTable columns={tableColumns} rows={records} rowKey="id" />}
      </TspCard>

      {canManage && (
        <div className="tsp-master-form-card">
          <div className="tsp-master-form-header">
            <div className="tsp-master-form-title-group">
              <span className="tsp-master-form-badge">Add Record</span>
              <h3 className="tsp-master-form-title">
                {formTitle || `Add New ${title || "Entry"}`}
              </h3>
              <p className="tsp-master-form-subtitle">
                {formSubtitle || `Fill in the verified details below to register a new ${title?.toLowerCase() || "entry"} in the directory.`}
              </p>
            </div>
          </div>

          {topHelper && <div className="tsp-master-form-top-helper">{topHelper}</div>}

          <div className="tsp-master-form-grid">
            {fields.map((field) => (
              <div
                key={field.key}
                className={`tsp-master-form-field ${field.fullWidth ? "tsp-field-full" : ""}`}
              >
                <label className="tsp-master-field-label" htmlFor={`tsp-field-${field.key}`}>
                  {field.type === "select" ? `Select ${field.label}` : field.label}
                  {field.required && <span className="tsp-required-star"> *</span>}
                </label>
                {field.type === "select" ? (
                  <select
                    id={`tsp-field-${field.key}`}
                    className="tsp-select tsp-select-block"
                    value={draft[field.key] || ""}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                  >
                    <option value="">{field.label}</option>
                    {field.options.map((option) => (
                      <option key={optionValue(option)} value={optionValue(option)}>
                        {optionLabel(option)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`tsp-field-${field.key}`}
                    type={field.type || "text"}
                    className="tsp-input"
                    placeholder={field.placeholder || field.label}
                    value={draft[field.key] || ""}
                    disabled={busy}
                    onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                  />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="tsp-form-error" role="alert" style={{ marginTop: "16px" }}>
              {error}
            </div>
          )}

          <div className="tsp-master-form-actions">
            <button
              type="button"
              className="tsp-btn tsp-btn-secondary"
              onClick={() => setDraft(emptyDraft)}
              disabled={busy}
            >
              <RefreshCw size={13} style={{ marginRight: "6px" }} /> Clear Form
            </button>
            <button
              type="button"
              className="tsp-btn tsp-btn-danger tsp-btn-submit"
              onClick={handleAddEntry}
              disabled={!requiredFilled || busy}
            >
              {busy ? "Saving…" : "Add Entry"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
